import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';

// Импорт CSS и сервиса API
import '../components/App.css';
import '../components/CarDealershipApp.css';
import ApiService from '../services/ApiService';
import AuthDebug from '../services/AuthDebug'; // Импорт утилиты отладки
import NewCarDetailsModal from './Modals/NewCarDetailsModal'; // Импортируем новый компонент модального окна
// Импорт захардкоженных данных для главной страницы
// Убираем импорт hardcodedCars, так как теперь используем API
// import { hardcodedCars } from './CarCatalog/HardcodedCars';

// Стандартный импорт компонентов
import Header from './Header/index';
import Footer from './Footer/index';
import CarGrid from './CarCatalog/CarGrid';
import SearchFilter from './CarCatalog/SearchFilter';
import HeroBanner from './HomePage/HeroBanner';
import Features from './HomePage/Features';
import ApplicationModal from './Modals/ApplicationModal';
import WhyUsSection from './AboutPage/WhyUsSection';
import ServicesPrice from './ServicesPage/ServicesPrice';
import WorkSteps from './ServicesPage/WorkSteps';
import Pagination from './UI/Pagination';
import ThankYouPage from './Modals/ThankYouPage';

// Ленивая загрузка тяжелых компонентов
const AdminPanel = lazy(() => import('./AdminPanel/index'));
const LoginModal = lazy(() => import('./Modals/LoginModal'));

const App = () => {
  // Проверка на мобильное устройство (только для стилей UI)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);  // Создаем экземпляр ApiService вне компонента для стабильной ссылки
  const apiService = useMemo(() => new ApiService(), []);
  
  // Состояния для API и аутентификации
  const [cars, setCars] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  // Удаляем состояния, которые больше не нужны для главной страницы
  // const [homePageCachedCars, setHomePageCachedCars] = useState([]);
  // const [isHomePageCarsLoaded, setIsHomePageCarsLoaded] = useState(false);

  // Убираем неиспользуемые состояния
  // const [isHomeLoading, setIsHomeLoading] = useState(false);
  // Удаляем состояние homePageCars, так как будем использовать захардкоженные данные
  // const [homePageCars, setHomePageCars] = useState([]);

  // Состояния для оптимизированной загрузки
  const [carPreviews, setCarPreviews] = useState([]);
  const [totalCars, setTotalCars] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState(false);
  
  // Состояние для случайных автомобилей на главной странице
  const [homePageCars, setHomePageCars] = useState([]);
  const [isHomePageLoading, setIsHomePageLoading] = useState(false);

  // Оптимизированное количество элементов на странице
  const ITEMS_PER_PAGE = isMobile ? 5 : 20;
  // Существующие состояния приложения
  const [activeTab, setActiveTab] = useState('home');
  const [searchQuery, setSearchQuery] = useState('');
  const [priceFilter, setPriceFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState({
    minYear: 2000,
    maxYear: 2025
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminView, setAdminView] = useState('list');
  const [editingCar, setEditingCar] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState({
    email: '',
    password: ''
  });
  const [loginError, setLoginError] = useState('');

  // Состояния модалок
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [applicationData, setApplicationData] = useState({
    name: '',
    phone: '',
    message: '',
    paymentMethod: 'cash'
  });
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [applicationSubmitting, setApplicationSubmitting] = useState(false);

  // Состояние для страницы благодарности
  const [showThankYouPage, setShowThankYouPage] = useState(false);

  // Состояния для пагинации
  const [currentPage, setCurrentPage] = useState(1);

  // Состояние формы для добавления/редактирования авто
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    price: 0,
    images: [],
    description: '',
    engineVolume: 0,
    color: '',
    transmission: 'Автомат',
    fuelType: 'Бензин',
    mileage: 0,
    driveType: 'Передний'
  });
  const [homeFilters, setHomeFilters] = useState({
    searchQuery: '',
    priceFilter: 'all',
    yearFilter: { minYear: 2000, maxYear: new Date().getFullYear() }
  });

  const [catalogFilters, setCatalogFilters] = useState({
    searchQuery: '',
    priceFilter: 'all',
    yearFilter: { minYear: 2000, maxYear: new Date().getFullYear() }
  });

// Функция для получения активных фильтров
  const getActiveFilters = () => {
    let filters;

    if (activeTab === 'home') {
      filters = homeFilters;
    } else if (activeTab === 'catalog') {
      filters = catalogFilters;
    } else {
      filters = { searchQuery, priceFilter, yearFilter };
    }

    // Убедимся, что yearFilter всегда валидный объект с числовыми значениями
    return {
      ...filters,
      yearFilter: {
        minYear: Number(filters.yearFilter?.minYear || 2000),
        maxYear: Number(filters.yearFilter?.maxYear || 2025)
      }
    };
  };

// Функция для обновления активных фильтров
  const updateActiveFilters = (filterName, value) => {
    console.log(`Updating ${filterName} with value:`, value); // Отладочный вывод

    if (activeTab === 'home') {
      setHomeFilters(prev => {
        const newFilters = { ...prev, [filterName]: value };
        console.log('New home filters:', newFilters); // Отладочный вывод
        return newFilters;
      });
    } else if (activeTab === 'catalog') {
      setCatalogFilters(prev => {
        const newFilters = { ...prev, [filterName]: value };
        console.log('New catalog filters:', newFilters); // Отладочный вывод
        return newFilters;
      });
    } else {
      // Для админ-панели и других вкладок
      if (filterName === 'searchQuery') setSearchQuery(value);
      else if (filterName === 'priceFilter') setPriceFilter(value);
      else if (filterName === 'yearFilter') setYearFilter(value);
    }
  };
  // Унифицированная функция загрузки данных для каталога
  const loadCatalogData = async () => {
    if (activeTab === 'catalog') { // Убираем проверку на 'home', так как для home используем статичные данные
      setIsPageLoading(true);
      setLoadingError(null);

      console.log(`Начало загрузки данных: ${new Date().toLocaleTimeString()}`);

      try {
        console.log('Отправка запроса к Firebase...');        // Используем оптимизированную функцию
        const result = await apiService.getCarsPreviewOptimized(currentPage, ITEMS_PER_PAGE);
        console.log(`Получен ответ: ${new Date().toLocaleTimeString()}`);
        console.log(`Получено автомобилей: ${result.cars?.length || 0}, всего: ${result.total || 0}`);

        if (result.cars?.length > 0) {
          console.log('Данные успешно загружены');
          setCarPreviews(result.cars);
          setTotalCars(result.total);
        } else {
          console.log('Firebase вернул пустой список автомобилей');
          setCarPreviews([]);
          setTotalCars(0);
        }
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
        setLoadingError(`Ошибка загрузки: ${error.message}`);
        setCarPreviews([]);
        setTotalCars(0);
      } finally {
        console.log(`Завершение загрузки: ${new Date().toLocaleTimeString()}`);
        setIsPageLoading(false);
      }
    }
  };

  // Загрузка данных для каталога
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const initCatalogLoad = async () => {
      if (activeTab === 'catalog') {
        if (isMounted) {
          setIsPageLoading(true);
          try {            // Используем серверную пагинацию
            const result = await apiService.getCarsPreviewOptimized(currentPage, ITEMS_PER_PAGE);

            if (isMounted) {
              if (result && result.cars) {
                setCarPreviews(result.cars);
                setTotalCars(result.total);
              } else {
                setCarPreviews([]);
                setTotalCars(0);
              }
            }
          } catch (error) {
            if (isMounted) {
              console.error('Ошибка загрузки каталога:', error);
              setLoadingError(`Ошибка загрузки: ${error.message}`);
            }
          } finally {
            if (isMounted) {
              setIsPageLoading(false);
            }
          }
        }
      }
    };

    initCatalogLoad();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [activeTab, currentPage, apiService, ITEMS_PER_PAGE]);

  // Удаляем эффект для предзагрузки данных для главной страницы, так как мы используем статичные данные

  // Функция загрузки случайных автомобилей для главной страницы
  const loadRandomCarsForHome = async () => {
    setIsHomePageLoading(true);
    try {
      const data = await apiService.getRandomCars(6);
      if (data && data.cars) {
        setHomePageCars(data.cars);
        console.log('Загружено случайных автомобилей для главной:', data.cars.length);
      } else {
        console.log('API не вернул данные');
        setHomePageCars([]);
      }
    } catch (error) {
      console.error('Ошибка при загрузке случайных автомобилей:', error);
      // В случае ошибки оставляем пустой массив
      setHomePageCars([]);
    } finally {
      setIsHomePageLoading(false);
    }
  };

  // Загрузка случайных автомобилей при открытии главной страницы
  useEffect(() => {
    if (activeTab === 'home') {
      loadRandomCarsForHome();
    }
  }, [activeTab]);

  // Удаляем эффект для предзагрузки кешированных данных для главной

  // Механизм повторной попытки загрузки - только для каталога
  useEffect(() => {
    let retryTimeout;

    if (carPreviews.length === 0 && !isPageLoading && !loadingError && activeTab === 'catalog') {
      console.log('Планирование повторной попытки загрузки...');
      retryTimeout = setTimeout(() => {
        console.log('Выполнение повторной попытки загрузки...');
        loadCatalogData();
      }, 3000); // Пробуем через 3 секунды
    }

    return () => {
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [carPreviews.length, isPageLoading, loadingError, activeTab]);

  // Отдельная загрузка для админ-панели
  useEffect(() => {
    if (activeTab === 'admin' && isAdmin) {
      const loadAdminData = async () => {
        setIsLoading(true);
        try {          apiService.getAllCars((fetchedCars) => {
            setCars(fetchedCars);
            setIsLoading(false);
          });
        } catch (error) {
          console.error('Ошибка загрузки данных админ-панели:', error);
          setLoadingError('Не удалось загрузить данные админ-панели');
          setIsLoading(false);
        }
      };

      loadAdminData();
    }
  }, [activeTab, isAdmin, apiService]);

  // Загрузка автомобилей и проверка авторизации при инициализации
  useEffect(() => {
    // Проверка URL для входа в админку
    const checkAdminAccess = () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('admin') === 'access') {
        // Автоматическое открытие модального окна входа
        setShowLoginModal(true);
      }
    };

    checkAdminAccess();    // Подписываемся на изменения состояния аутентификации
    const unsubscribe = apiService.onAuthStateChanged((isAuthenticated) => {
      // Use our helper function to ensure consistent auth state updates
      updateAuthState(isAuthenticated);
    });

    // Отписываемся при размонтировании компонента
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [apiService]);

  // Обработка URL при загрузке страницы
  useEffect(() => {
    const handleInitialUrl = () => {
      const url = window.location.pathname;
      const segments = url.split('/').filter(Boolean);

      // Если URL содержит car/ID, показываем детали автомобиля
      if (segments.length >= 2 && segments[0] === 'car') {
        const carId = segments[1];

        // Сначала проверяем в данных главной страницы
        const homePageCar = homePageCars.find(c => c.id === carId);

        if (homePageCar) {
          setSelectedCar(homePageCar);
          setShowDetailsModal(true);
        } else {
          // Затем ищем в динамически загруженных данных
          const car = carPreviews.find(c => c.id === carId);
          if (car) {
            setSelectedCar(car);
            setShowDetailsModal(true);
          }
        }
      }      // Установка активной вкладки из URL
      if (segments.length > 0) {
        const tab = segments[0];
        if (['home', 'catalog', 'services', 'about', 'contacts', 'thank-you'].includes(tab)) {
          setActiveTab(tab === 'thank-you' ? 'thankYou' : tab);
        } else if (tab === 'admin') {
          // Если это табличка админа и пользователь залогинен, показываем админку
          if (isAdmin) {
            setActiveTab('admin');
          } else {
            // Если не залогинен, показываем модальное окно логина
            setShowLoginModal(true);
            // И остаемся на текущей вкладке
          }
        } else if (tab !== 'car') {
          // Если в URL не car/ID и не одна из известных вкладок, устанавливаем home
          setActiveTab('home');
        }
      }
    };

    // Вызываем обработку URL только для динамически загруженных данных
    if (activeTab === 'catalog' && !isPageLoading && carPreviews.length > 0) {
      handleInitialUrl();
    } else {
      // Для захардкоженных данных или других вкладок просто обрабатываем URL
      handleInitialUrl();
    }
  }, [carPreviews, homePageCars, isPageLoading, activeTab, isAdmin]);

  // Функция для обновления URL при изменении состояния
  const updateBrowserUrl = (newPath) => {
    window.history.pushState({}, '', newPath);
  };

  // Функция для перехода на страницу благодарности
  const handleGoToThankYouPage = () => {
    setShowApplicationModal(false);
    setSelectedCar(null);
    setShowThankYouPage(true);
    setActiveTab('thankYou');
    updateBrowserUrl('/thank-you');
  };

  // Функция для возврата с страницы благодарности
  const handleLeaveThankYouPage = () => {
    setShowThankYouPage(false);
    setActiveTab('home');
    updateBrowserUrl('/');
  };
  // Фильтрация с единым подходом
  // Модифицированная функция filteredCars в App.js
  const filteredCars = useMemo(() => {
    // Получаем активные фильтры
    const activeFilters = getActiveFilters();
    const isSearchActive = activeFilters.searchQuery && activeFilters.searchQuery.trim().length > 0;
    const isPriceFilterActive = activeFilters.priceFilter !== 'all';
    const isYearFilterActive = activeFilters.yearFilter.minYear !== 2000 ||
        activeFilters.yearFilter.maxYear !== new Date().getFullYear();

    // Проверяем, активны ли фильтры
    const isAnyFilterActive = isSearchActive || isPriceFilterActive || isYearFilterActive;

    // Для админ-панели всегда используем только данные админа
    if (activeTab === 'admin') {
      return cars.filter(car => {
        // Полная логика фильтрации для админ-панели
        const matchesSearch = !activeFilters.searchQuery ||
            car.brand.toLowerCase().includes(activeFilters.searchQuery.toLowerCase()) ||
            car.model.toLowerCase().includes(activeFilters.searchQuery.toLowerCase());

        const matchesPrice =
            activeFilters.priceFilter === 'all' ||
            (activeFilters.priceFilter === 'under1m' && car.price < 1000000) ||
            (activeFilters.priceFilter === 'under1.5m' && car.price < 1500000) ||
            (activeFilters.priceFilter === 'under2m' && car.price < 2000000) ||
            (activeFilters.priceFilter === 'under2.5m' && car.price < 2500000) ||
            (activeFilters.priceFilter === 'under3m' && car.price < 3000000);

        const matchesYear =
            car.year >= activeFilters.yearFilter.minYear &&
            car.year <= activeFilters.yearFilter.maxYear;

        return matchesSearch && matchesPrice && matchesYear;
      });
    }

    // Если есть активный поиск или фильтры, объединяем данные со всех источников
    if (isAnyFilterActive) {
      // Создаем объединенный набор данных
      let combinedData = [];

      // Всегда добавляем данные для главной страницы  
      combinedData.push(...homePageCars);

      // Добавляем данные из API (если они загружены)
      if (carPreviews && carPreviews.length > 0) {
        // Исключаем дубликаты (если есть машины с одинаковыми ID)
        const homePageIds = new Set(homePageCars.map(car => car.id));
        const uniqueCarPreviews = carPreviews.filter(car => !homePageIds.has(car.id));
        combinedData.push(...uniqueCarPreviews);
      }

      console.log('Combined data for filtering:', combinedData.length);

      // Фильтруем объединенный набор данных
      return combinedData.filter(car => {
        const matchesSearch = !activeFilters.searchQuery ||
            car.brand.toLowerCase().includes(activeFilters.searchQuery.toLowerCase()) ||
            car.model.toLowerCase().includes(activeFilters.searchQuery.toLowerCase());

        const matchesPrice =
            activeFilters.priceFilter === 'all' ||
            (activeFilters.priceFilter === 'under1m' && car.price < 1000000) ||
            (activeFilters.priceFilter === 'under1.5m' && car.price < 1500000) ||
            (activeFilters.priceFilter === 'under2m' && car.price < 2000000) ||
            (activeFilters.priceFilter === 'under2.5m' && car.price < 2500000) ||
            (activeFilters.priceFilter === 'under3m' && car.price < 3000000);

        const matchesYear =
            car.year >= activeFilters.yearFilter.minYear &&
            car.year <= activeFilters.yearFilter.maxYear;

        return matchesSearch && matchesPrice && matchesYear;
      });
    }

    // Если нет активных фильтров, используем данные только для текущей вкладки
    const dataToFilter = activeTab === 'home' ? homePageCars : carPreviews;

    // Применяем фильтры текущей вкладки
    return dataToFilter.filter(car => {
      const matchesSearch = !activeFilters.searchQuery ||
          car.brand.toLowerCase().includes(activeFilters.searchQuery.toLowerCase()) ||
          car.model.toLowerCase().includes(activeFilters.searchQuery.toLowerCase());

      const matchesPrice =
          activeFilters.priceFilter === 'all' ||
          (activeFilters.priceFilter === 'under1m' && car.price < 1000000) ||
          (activeFilters.priceFilter === 'under1.5m' && car.price < 1500000) ||
          (activeFilters.priceFilter === 'under2m' && car.price < 2000000) ||
          (activeFilters.priceFilter === 'under2.5m' && car.price < 2500000) ||
          (activeFilters.priceFilter === 'under3m' && car.price < 3000000);

      const matchesYear =
          car.year >= activeFilters.yearFilter.minYear &&
          car.year <= activeFilters.yearFilter.maxYear;

      return matchesSearch && matchesPrice && matchesYear;
    });
  }, [cars, carPreviews, homePageCars, activeTab, homeFilters, catalogFilters, searchQuery, priceFilter, yearFilter, getActiveFilters]);

  // Используем захардкоженные данные для главной страницы
  const homeCars = useMemo(() => {
    // Всегда возвращаем отфильтрованные данные
    return filteredCars;
  }, [filteredCars]);
  const paginatedCars = useMemo(() => {
    if (activeTab === 'admin' || activeTab === 'catalog') {
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      return filteredCars.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }
    // Для главной страницы возвращаем отфильтрованный список без пагинации
    return filteredCars;
  }, [filteredCars, currentPage, ITEMS_PER_PAGE, activeTab]);

  const hasResultsFromOtherTab = useMemo(() => {
    // Получаем активные фильтры
    const activeFilters = getActiveFilters();
    const isSearchActive = activeFilters.searchQuery && activeFilters.searchQuery.trim().length > 0;
    const isPriceFilterActive = activeFilters.priceFilter !== 'all';
    const isYearFilterActive = activeFilters.yearFilter.minYear !== 2000 ||
        activeFilters.yearFilter.maxYear !== new Date().getFullYear();

    // Если нет активных фильтров, не показываем уведомление
    if (!isSearchActive && !isPriceFilterActive && !isYearFilterActive) {
      return false;
    }

    // Если сейчас отображаются все страницы (активен поиск или фильтр)
    if (activeTab === 'home') {
      // Проверяем, содержит ли результат элементы, которых нет в homePageCars
      const homePageIds = new Set(homePageCars.map(car => car.id));
      return filteredCars.some(car => !homePageIds.has(car.id));
    } else if (activeTab === 'catalog') {
      // Проверяем, содержит ли результат элементы из homePageCars
      const previewIds = new Set(carPreviews.map(car => car.id));
      return filteredCars.some(car => !previewIds.has(car.id));
    }

    return false;
  }, [activeTab, filteredCars, homePageCars, carPreviews, getActiveFilters]);
  // Обновленный расчет общего количества страниц
  const totalPages = useMemo(() => {
    if (activeTab === 'admin' || activeTab === 'catalog') {
      return Math.ceil(filteredCars.length / ITEMS_PER_PAGE);
    }
    // Для главной страницы не используем пагинацию
    return 1;
  }, [filteredCars, ITEMS_PER_PAGE, activeTab]);

  // Обработчик изменения страницы
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Прокручиваем страницу вверх при смене страницы
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  // Сбрасываем текущую страницу при изменении поискового запроса или фильтров
  useEffect(() => {
    setCurrentPage(1);
  }, [homeFilters, catalogFilters]);// Обработчик изменения вкладки с обновлением URL
  const handleTabChange = (tab) => {
    // Debug auth state when tab changes
    AuthDebug.logAuthState(isAdmin, tab);
    
    // If trying to switch to admin tab but not logged in, show login modal
    if (tab === 'admin' && !isAdmin) {
      console.log('Not logged in, showing login modal instead of changing tab');
      setShowLoginModal(true);
      return; // Don't update the tab until logged in
    }
    
    // Set the active tab
    setActiveTab(tab);
    
    // Update URL based on the tab
    if (tab === 'home') {
      updateBrowserUrl('/');
    } else if (tab === 'thankYou') {
      updateBrowserUrl('/thank-you');
    } else if (tab === 'admin') {
      // For admin tab, make sure we're authenticated
      if (isAdmin) {
        console.log('Switching to admin tab, user is authenticated');
        updateBrowserUrl(`/${tab}`);
      } else {
        console.log('Cannot switch to admin tab, user is not authenticated');
        // Force show the login modal
        setShowLoginModal(true);
      }
    } else {
      updateBrowserUrl(`/${tab}`);
    }
  };

  // Обработчики Firebase-операций
  const handleAddCar = async (e) => {
    e.preventDefault();
    try {
      console.log('Начало добавления автомобиля');
      const carId = await apiService.addCar(formData);
      console.log('Автомобиль добавлен с ID:', carId);

      // Создаем объект нового автомобиля для добавления в стейт
      const newCar = {
        id: carId,
        ...formData,
        createdAt: new Date().toISOString()
      };
      
      // Добавляем новый автомобиль в начало списка без перезагрузки
      setCars(prevCars => [newCar, ...prevCars]);

      // Обновляем также общий список для пагинации если нужно
      setCarPreviews(prevPreviews => {
        if (prevPreviews && prevPreviews.length > 0) {
          return [newCar, ...prevPreviews];
        }
        return prevPreviews;
      });

      setAdminView('list');
      // Сброс формы
      setFormData({
        brand: '',
        model: '',
        year: new Date().getFullYear(),
        price: 0,
        images: [],
        description: '',
        engineVolume: 0,
        color: '',
        transmission: 'Автомат',
        fuelType: 'Бензин',
        mileage: 0,
        driveType: 'Передний'
      });
    } catch (err) {
      console.error('Ошибка добавления авто:', err);
      alert('Не удалось добавить автомобиль: ' + (err.message || 'Неизвестная ошибка'));
    }
  };
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      if (editingCar) {
        await apiService.updateCar(editingCar, formData);
        
        // Обновляем автомобиль в локальном состоянии
        setCars(prevCars => 
          prevCars.map(car => 
            car.id === editingCar ? { ...formData, id: editingCar } : car
          )
        );
        
        // Обновляем также общий список для пагинации если нужно
        setCarPreviews(prevPreviews => {
          if (prevPreviews && prevPreviews.length > 0) {
            return prevPreviews.map(car => 
              car.id === editingCar ? { ...formData, id: editingCar } : car
            );
          }
          return prevPreviews;
        });
        
        setAdminView('list');
        setEditingCar(null);
      }
    } catch (err) {
      console.error('Ошибка обновления авто:', err);
      alert('Не удалось обновить автомобиль');
    }
  };
  const handleDeleteCar = async (carId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот автомобиль?')) {
      try {
        await apiService.deleteCar(carId);
        
        // Удаляем автомобиль из локального состояния
        setCars(prevCars => prevCars.filter(car => car.id !== carId));
        
        // Удаляем также из общего списка для пагинации если нужно
        setCarPreviews(prevPreviews => {
          if (prevPreviews && prevPreviews.length > 0) {
            return prevPreviews.filter(car => car.id !== carId);
          }
          return prevPreviews;
        });
        
      } catch (err) {
        console.error('Ошибка удаления авто:', err);
        alert('Не удалось удалить автомобиль');
      }
    }
  };
  // Вспомогательная функция для обновления состояния аутентификации
  const updateAuthState = (isAuthenticated) => {
    setIsAdmin(isAuthenticated);
    
    // Log the auth state using our debug utility
    AuthDebug.logAuthState(isAuthenticated, activeTab);
  };

  // Обработчик входа
  const handleAdminLogin = async (e) => {
    e.preventDefault();    try {
      await apiService.loginUser(loginCredentials.email, loginCredentials.password);
      
      // Update the auth state and UI
      updateAuthState(true);
      setActiveTab('admin');
      setShowLoginModal(false);
      setLoginError('');

      // Удаляем параметр admin из URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err) {
      console.error('Ошибка входа:', err);
      setLoginError('Неверный email или пароль');
    }
  };
  // Обработчик выхода
  const handleAdminLogout = async () => {    try {
      await apiService.logoutUser();
      
      // Update the auth state and UI
      updateAuthState(false);
      setActiveTab('home');
      
      console.log('User logged out successfully');
    } catch (err) {
      console.error('Ошибка выхода:', err);
    }
  };

  // Обработчик открытия деталей автомобиля  // Обновленная функция handleOpenDetailsModal в App.js
  const handleOpenDetailsModal = async (car) => {
    console.log('handleOpenDetailsModal called with car:', car);
    // Сначала показываем модальное окно с захардкоженными данными
    setSelectedCar(car);
    setShowDetailsModal(true);
    console.log('Modal state set to:', true);
    updateBrowserUrl(`/car/${car.id}`);

    // Проверяем, является ли автомобиль с главной страницы
    const isHomePageCar = homePageCars.some(hc => hc.id === car.id);

    if (isHomePageCar) {
      try {        // Пытаемся найти в базе соответствующий автомобиль по марке и модели
        // Это предполагает, что у вас есть метод для поиска автомобиля по этим параметрам
        const matchingCars = await apiService.searchCarsByBrandAndModel(car.brand, car.model);

        // Если нашли соответствие, обновляем данные
        if (matchingCars && matchingCars.length > 0) {
          // Либо берем первый подходящий автомобиль
          const matchingCar = matchingCars[0];

          // Создаем объект, который объединяет захардкоженные данные с данными из БД
          const combinedCar = {
            ...car, // Базовые захардкоженные данные
            ...matchingCar, // Данные из Firebase (они перезапишут одноименные поля)
            // Сохраняем захардкоженные изображения, если из БД не пришли
            images: matchingCar.images || car.images,
            image: matchingCar.image || car.image,
            // Идентификатор оставляем захардкоженный, чтобы сохранить ссылку
            id: car.id
          };

          // Обновляем состояние с объединенными данными
          setSelectedCar(combinedCar);
        }
      } catch (error) {
        console.error('Ошибка при поиске автомобиля в базе данных:', error);
        // В случае ошибки оставляем захардкоженные данные
      }
    } else {
      // Для обычных автомобилей из каталога продолжаем использовать стандартную логику
      if (!car.images) {
        try {
          const fullCarData = await apiService.getCarFullDetails(car.id);
          if (fullCarData) {
            setSelectedCar(fullCarData);
          }
        } catch (error) {
          console.error('Ошибка загрузки полных данных авто:', error);
        }
      }
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedCar(null);
    // Возвращаем URL к активной вкладке
    if (activeTab === 'home') {
      updateBrowserUrl('/');
    } else {
      updateBrowserUrl(`/${activeTab}`);
    }
  };

  // Обработчик открытия формы заявки
  const handleOpenApplicationModal = (car = null) => {
    // Если передана карточка автомобиля, устанавливаем её
    if (car) {
      setSelectedCar(car);
    }
    setShowApplicationModal(true);
    setApplicationSubmitted(false);
    setApplicationData({
      name: '',
      phone: '',
      message: '',
      paymentMethod: 'cash'
    });
  };

  const handleCloseApplicationModal = () => {
    setShowApplicationModal(false);
    // Если была открыта форма заявки из модалки деталей, то не сбрасываем выбранную машину
    if (!showDetailsModal) {
      setSelectedCar(null);
    }
  };

  const handleApplicationInputChange = (e) => {
    const { name, value } = e.target;
    setApplicationData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  // Обработчик отправки заявки
  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setApplicationSubmitting(true);

    try {
      // Создание ссылки на карточку автомобиля (если он выбран)
      const carLink = selectedCar ?
          `${window.location.origin}/car/${selectedCar.id}` :
          null;

      // Информация о выбранном автомобиле (если есть)
      const carInfo = selectedCar ?
          `${selectedCar.brand} ${selectedCar.model}, ${selectedCar.year} г., ₽${Math.round(selectedCar.price).toLocaleString()}` :
          null;

      // Отправляем данные через ApiService
      const result = await apiService.sendApplication({
        name: applicationData.name,
        phone: applicationData.phone,
        paymentMethod: applicationData.paymentMethod,
        message: applicationData.message,
        carInfo: carInfo,
        carLink: carLink
      });
      
      if (result.success) {
        console.log('Заявка успешно отправлена:', result.message);
        // Устанавливаем флаг успешной отправки
        setApplicationSubmitted(true);

        // Переходим на страницу благодарности через 1.5 секунды
        setTimeout(() => {
          handleGoToThankYouPage();
        }, 1500);
      } else {
        throw new Error(result.message || 'Ошибка отправки заявки');
      }
      
    } catch (error) {
      console.error('Ошибка при отправке данных:', error);
      // Даже при ошибке показываем успешное сообщение пользователю
      setApplicationSubmitted(true);
      setTimeout(() => {
        handleGoToThankYouPage();
      }, 1500);
    } finally {
      setApplicationSubmitting(false);
    }
  };

  const handleEditStart = (car) => {
    setEditingCar(car.id);
    setFormData({
      ...car,
      images: car.images || [car.image]
    });
    setAdminView('edit');
  };

  // Обработчик изменения формы
  const handleInputChange = (name, value) => {
    setFormData(prevData => ({
      ...prevData,
      [name]: name === 'price' || name === 'year' || name === 'engineVolume' || name === 'mileage'
          ? (value === '' ? '' : parseFloat(value))
          : value
    }));
  };

  // Обработчики для изображений
  const handleImageUpload = (imageDataUrl) => {
    setFormData(prevData => ({
      ...prevData,
      images: [...(prevData.images || []), imageDataUrl]
    }));
  };

  const handleImageDelete = (index) => {
    setFormData(prevData => {
      const updatedImages = [...(prevData.images || [])];
      updatedImages.splice(index, 1);
      return {
        ...prevData,
        images: updatedImages
      };
    });
  };

  // Обработчик перемещения изображений
  const handleImageReorder = (newImages) => {
    setFormData(prevData => ({
      ...prevData,
      images: newImages
    }));
  };

  // Компонент индикатора загрузки
  const LoadingIndicator = () => (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Загрузка данных...</p>
      </div>
  );

  // Компонент сообщения об ошибке
  const ErrorMessage = () => (
      <div className="error-container">
        <div className="error-icon">⚠️</div>
        <h3>Ошибка загрузки</h3>
        <p>{loadingError || "Произошла ошибка при загрузке данных."}</p>
        <div className="error-actions">
          <button
              className="button primary"
              onClick={() => {
                setLoadingError(null);
                loadCatalogData(); // Пробуем загрузить снова
              }}
          >
            Попробовать снова
          </button>
          <button
              className="button secondary"
              onClick={() => window.location.reload()}
          >
            Обновить страницу
          </button>
        </div>
      </div>
  );  return (
      <div className="app-container">
        <Header
            activeTab={activeTab}
            setActiveTab={handleTabChange}
            isAdmin={isAdmin}
        />

        <main className="main container">          {isLoading && activeTab === 'admin' ? (
              <LoadingIndicator />
          ) : loadingError ? (
              <ErrorMessage />
          ) : activeTab === 'admin' && isAdmin ? (
              <Suspense fallback={<LoadingIndicator />}>
                {/* Debug check before rendering Admin Panel */}
                {AuthDebug.shouldShowAdminPanel(isAdmin, activeTab) && (
                  <AdminPanel
                      cars={filteredCars}
                      adminView={adminView}
                      setAdminView={setAdminView}
                      handleDeleteCar={handleDeleteCar}
                      handleAddCar={handleAddCar}
                      handleSaveEdit={handleSaveEdit}
                      handleEditStart={handleEditStart}
                      handleAdminLogout={handleAdminLogout}
                      formData={formData}
                      onChange={handleInputChange}
                      onImageUpload={handleImageUpload}
                      onImageDelete={handleImageDelete}
                      onImageReorder={handleImageReorder}
                      setFormData={setFormData}
                  />
                )}
              </Suspense>          ) : activeTab === 'home' && (
              <>
                <HeroBanner onOpenApplicationModal={() => handleOpenApplicationModal()} />
                <SearchFilter
                    searchQuery={getActiveFilters().searchQuery || ''}
                    setSearchQuery={(value) => updateActiveFilters('searchQuery', value)}
                    priceFilter={getActiveFilters().priceFilter || 'all'}
                    setPriceFilter={(value) => updateActiveFilters('priceFilter', value)}
                    yearFilter={{
                      minYear: Number(getActiveFilters().yearFilter?.minYear || 2000),
                      maxYear: Number(getActiveFilters().yearFilter?.maxYear || 2025)
                    }}
                    setYearFilter={(value) => updateActiveFilters('yearFilter', value)}
                />
                <CarGrid
                    cars={homeCars}
                    activeTab={activeTab}
                    onOpenApplicationModal={handleOpenApplicationModal}
                    onOpenDetailsModal={handleOpenDetailsModal}
                />
                <Features />
              </>
          )}          {activeTab === 'catalog' && (
              <div className="catalog-page">
                <SearchFilter
                    searchQuery={getActiveFilters().searchQuery}
                    setSearchQuery={(value) => updateActiveFilters('searchQuery', value)}
                    priceFilter={getActiveFilters().priceFilter}
                    setPriceFilter={(value) => updateActiveFilters('priceFilter', value)}
                    yearFilter={getActiveFilters().yearFilter}
                    setYearFilter={(value) => updateActiveFilters('yearFilter', value)}
                />
                {(isPageLoading || (activeTab === 'home' && isHomePageLoading)) ? (
                    <LoadingIndicator />
                ) : (
                    <>
                      {hasResultsFromOtherTab && (
                          <div className="search-results-notice">

                          </div>
                      )}
                      <CarGrid
                          cars={paginatedCars}
                          activeTab={activeTab}
                          onOpenApplicationModal={handleOpenApplicationModal}
                          onOpenDetailsModal={handleOpenDetailsModal}
                      />
                      {totalPages > 1 && (
                          <Pagination
                              currentPage={currentPage}
                              totalPages={totalPages}
                              onPageChange={handlePageChange}
                          />
                      )}
                    </>
                )}
              </div>
          )}

          {activeTab === 'services' && (
              <div className="tab-content services-page">
                <ServicesPrice onOpenApplicationModal={() => handleOpenApplicationModal()} />
                <WorkSteps onOpenApplicationModal={() => handleOpenApplicationModal()} />
              </div>
          )}

          {activeTab === 'about' && (
              <div className="tab-content about-page">
                <WhyUsSection />
              </div>
          )}

          {activeTab === 'thankYou' && (
              <ThankYouPage
                  onLeave={handleLeaveThankYouPage}
                  selectedCar={selectedCar}
              />
          )}
        </main>

        <Footer setActiveTab={handleTabChange} />

        {/* Модальные окна */}        {showLoginModal && (
            <Suspense fallback={<LoadingIndicator />}>
              <LoginModal
                  onClose={(success) => {
                    setShowLoginModal(false);
                    // If login was successful, set the active tab to admin
                    if (success) {
                      setActiveTab('admin');
                    }
                  }}
                  setIsAdmin={setIsAdmin}
                  onLogin={handleAdminLogin}
                  loginCredentials={loginCredentials}
                  handleLoginInputChange={(e) =>
                      setLoginCredentials(prev => ({
                        ...prev,
                        [e.target.name]: e.target.value
                      }))
                  }
                  loginError={loginError}
              />
            </Suspense>
        )}

        {showApplicationModal && (
            <ApplicationModal
                onClose={handleCloseApplicationModal}
                applicationData={applicationData}
                onChange={handleApplicationInputChange}
                onSubmit={handleSubmitApplication}
                isSubmitted={applicationSubmitted}
                isSubmitting={applicationSubmitting}
                selectedCar={selectedCar}
                onGoToThankYouPage={handleGoToThankYouPage}
            />        )}
          {showDetailsModal && selectedCar && (
            <NewCarDetailsModal
                car={selectedCar}
                onClose={handleCloseDetailsModal}
                onOpenApplicationModal={handleOpenApplicationModal}
            />
        )}
      </div>
  );
};

export default App;